import { OpenConfirmModal } from 'node_modules/@mantine/modals/lib/context';
import { proxy, subscribe } from 'valtio';

import { DIAGRAM_SIDEBAR_WIDTH } from '@diagram/diagram-dimensions';
import { SidebarService } from '@diagram/services/SidebarService';
import { state as selectionState } from '@diagram/state/selection';

import { LocalStorageService } from 'services/LocalStorageService';
import { state as currentProjectState } from 'state/current-project';

export enum SidebarType {
    'AI' = 'ai',
    'ADD' = 'add',
    'EDIT_COMPONENT' = 'editComponent',
    'EDIT_COMPONENT_INSTANCES' = 'editComponentInstances',
    'EDIT_CONNECTION' = 'editConnection',
    'EDIT_CONNECTIONS' = 'editConnections',
    'EDIT_INFO_BLOCK' = 'editInfoBlock',
    'COMMENTS' = 'comments',
    'CHAT' = 'chat',
    'BILL_OF_MATERIALS' = 'billOfMaterials',
    'FILES' = 'files',
    'GETTING_STARTED' = 'gettingStarted',
    'SHARE' = 'share',
    'PROJECT' = 'project',
    'SIMULATIONS' = 'simulations',
    'FAULT_SIMULATIONS' = 'faultSimulations',
    'SIMULATIONS_CREATE' = 'simulationsCreate',
    'SIMULATION_PROFILES' = 'simulationProfiles',
    'BATCH_SIMULATION_GRAPH' = 'batchSimulationGraph',
    'VALIDATIONS' = 'validations',
    'WIRE_SCHEDULE' = 'wireSchedule',
    'LIVE_DATA' = 'liveData',
    'SNIPPETS' = 'snippets',
}

export enum SidebarTab {
    SPECIFICATIONS = 'specifications',
    SIMULATION = 'simulation',
    FAULT_SIMULATION = 'faultSimulation',
    LIVE_DATA = 'liveData',
    CONTROLS = 'controls',
    NOTES = 'notes',
    SETIINGS = 'settings',
    INSTALLATION = 'installation',
    WIRE_SIZING_ASSISTANT = 'wireSizingAssistant',
    CONNECT_CABLE = 'connectCable',
    COMMENTS = 'comments',
    FILES = 'files',
    DATASHEET = 'datasheet',
    COMPONENTS = 'components',
    QUOTES = 'quotes',
    SIGN_OFF = 'signOff',
    AI = 'ai',
    ADD_GENERIC = 'addGeneric',
    ADD_TEAM = 'addTeam',
    ADD_CATALOG = 'addCatalog',
    ADD_FAVORITE = 'addFavorite',
    LIVE_DATA_LIVE = 'live-data-live',
    LIVE_DATA_LOGS = 'live-data-logs',
}

export type SidebarConfirmModal = Omit<OpenConfirmModal, 'onConfirm' | 'onCancel'> & {
    onConfirm?: () => Promise<void>;
    onCancel?: () => Promise<void>;
};

const EDIT_SIDEBAR_TYPES = [
    SidebarType.EDIT_COMPONENT,
    SidebarType.EDIT_COMPONENT_INSTANCES,
    SidebarType.EDIT_CONNECTION,
    SidebarType.EDIT_CONNECTIONS,
    SidebarType.EDIT_INFO_BLOCK,
];

const TABBED_SIDEBAR_TYPES = [
    SidebarType.ADD,
    ...EDIT_SIDEBAR_TYPES,
    SidebarType.BILL_OF_MATERIALS,
    SidebarType.FILES,
    SidebarType.LIVE_DATA,
];

const defaultValues = {
    collapsed: false,
    type: SidebarType.GETTING_STARTED,
    tab: null,
    width: DIAGRAM_SIDEBAR_WIDTH,
    confirmModal: null,
};

const state = proxy<{
    collapsed: boolean;
    type: SidebarType;
    tab: SidebarTab | null;
    width: number;
    confirmModal: SidebarConfirmModal | null;
}>(defaultValues);

const persist = () => {
    const projectId = currentProjectState.project?.id;

    if (!projectId) {
        return;
    }

    const map = SidebarService.getSavedState();

    map[projectId] = {
        type: state.type,
        tab: state.tab ?? undefined,
        collapsed: state.collapsed,
        selection: EDIT_SIDEBAR_TYPES.includes(state.type) ? selectionState.selection.map((item) => ({ ...item })) : [],
    };

    LocalStorageService.store(LOCAL_STORAGE_KEY, map);
};

subscribe(state, persist);
subscribe(selectionState, persist);

const reset = () => {
    Object.keys(state).forEach((key) => {
        // @ts-ignore
        state[key] = defaultValues[key];
    });
};

export const LOCAL_STORAGE_KEY = 'dcide-project-sidebar';

export { EDIT_SIDEBAR_TYPES, reset, state, TABBED_SIDEBAR_TYPES };
